import { useCallback, useMemo } from "react";

import type { ShadowLocation } from "../types/location-map";

import { createLocationMarkerFeature } from "../map/markers/create-marker-feature";
import { buildLocationMapOverlays } from "../utils/location/location-map-features";
import { useMapCoordinateInputs } from "./useMapCoordinateInputs";

type UseLocationFormMapArgs = {
	latitude: string;
	/** When set, zone overlays are shown on the map (edit mode). */
	location?: ShadowLocation;
	locationId: string;
	locationName?: string;
	longitude: string;
	mapCenter?: [number, number] | null;
	name: string;
	reverseGeocodeAt: (coordinates: [number, number]) => void;
};

/**
 * Shared map marker, overlays, and geocode handlers for create/edit location dialogs.
 */
export const useLocationFormMap = ({
	latitude,
	location,
	locationId,
	locationName,
	longitude,
	mapCenter,
	name,
	reverseGeocodeAt,
}: UseLocationFormMapArgs) => {
	const {
		hasPartialCoordinates,
		mapFrameCenter,
		onMoveEnd,
		parsedCoordinates,
		viewportCenter,
	} = useMapCoordinateInputs({
		fallbackCenter: location?.coordinates?.coordinates,
		latitude,
		longitude,
		mapCenter,
	});

	const locationMarker = useMemo((): GeoJSON.Feature | undefined => {
		if (!parsedCoordinates) return undefined;
		const feature = createLocationMarkerFeature({
			coordinates: parsedCoordinates,
			id: locationId,
			name: name.trim() || locationName || "New location",
		});
		return {
			...feature,
			properties: {
				...feature.properties,
				editable: true,
			},
		};
	}, [locationId, locationName, name, parsedCoordinates]);

	const mapMarkers = useMemo(
		(): GeoJSON.Feature[] | undefined =>
			locationMarker ? [locationMarker] : undefined,
		[locationMarker],
	);

	const mapOverlays = useMemo(
		() => (location ? buildLocationMapOverlays(location) : undefined),
		[location],
	);

	const handleMapClick = useCallback(
		(coordinates: [number, number]) => {
			reverseGeocodeAt(coordinates);
		},
		[reverseGeocodeAt],
	);

	const handleMarkerDragEnd = useCallback(
		(_feature: GeoJSON.Feature, coordinates: [number, number]) => {
			reverseGeocodeAt(coordinates);
		},
		[reverseGeocodeAt],
	);

	const handleUseMapCenter = useCallback(() => {
		if (!viewportCenter) return;
		reverseGeocodeAt(viewportCenter);
	}, [reverseGeocodeAt, viewportCenter]);

	return {
		handleMapClick,
		handleMarkerDragEnd,
		handleUseMapCenter,
		hasPartialCoordinates,
		locationMarker,
		mapFrameCenter,
		mapMarkers,
		mapOverlays,
		onMoveEnd,
		parsedCoordinates,
		viewportCenter,
	};
};
